import { Button, Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui';
import { useI18n } from '@/context/I18nProvider';
import { useBoolean } from '@/hooks/useBoolean';
import { ApiClient } from '@/services/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useState } from 'react';
import { toast } from 'sonner';
type ToggleAIConversationDialogProps = {
  conversationId: string;
  isAIEnabled: boolean;
  onConfirm?: () => Promise<void>;
  trigger: ReactNode;
  title?: string;
  description?: string;
  shouldOpenDialog?: boolean;
};
const ToggleAIConversationDialog = ({
  conversationId,
  isAIEnabled,
  trigger,
  onConfirm = async () => {},
  title,
  description,
  shouldOpenDialog = true
}: ToggleAIConversationDialogProps) => {
  const {
    t
  } = useI18n();
  const queryClient = useQueryClient();
  const showDialog = useBoolean(false);
  const [isLoading, setIsLoading] = useState(false);
  const defaultTitle = title || (isAIEnabled ? t('conversations.disableAI') : t('conversations.enableAI'));
  const defaultDescription = description || (isAIEnabled ? t('conversations.disableAIConfirm') : t('conversations.enableAIConfirm'));

  // ------------------- MUTATIONS -----------------------------------

  const mToggleAI = useMutation({
    mutationFn: () => {
      const newAIState = !isAIEnabled;
      return ApiClient.lead.updateConversationAI({
        leadId: conversationId,
        enableAIRequest: {
          EnableAI: newAIState
        }
      });
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: ['lead.getLead', {
          id: conversationId
        }]
      });
      await onConfirm();
      showDialog.onFalse();
      toast.success(isAIEnabled ? t('conversations.aiDisabledSuccess') : t('conversations.aiEnabledSuccess'));
    },
    onError: () => {
      showDialog.onFalse();
      toast.error(t('conversations.aiToggleError'));
    }
  });

  // ------------------- HANDLERS -----------------------------------

  const handleToggleAI = async () => {
    setIsLoading(true);
    try {
      await mToggleAI.mutateAsync();
    } catch {
      toast.error(t('conversations.aiToggleError'));
    } finally {
      setIsLoading(false);
    }
  };
  const handleOpenChange = async () => {
    if (!shouldOpenDialog && !showDialog.value) {
      await onConfirm();
      return;
    }
    showDialog.onToggle();
  };

  // ------------------- RENDER -----------------------------------

  return <Dialog open={showDialog.value} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>

      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{defaultTitle}</DialogTitle>
          <DialogDescription>
            {defaultDescription.split('\n').map(line => <span key={line} className="block">
                {line}
              </span>)}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="sm:justify-end">
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={isLoading}>
              {t('common.cancel')}
            </Button>
          </DialogClose>

          <Button type="button" variant={isAIEnabled ? 'destructive' : 'default'} onClick={handleToggleAI} disabled={isLoading}>
            {isLoading ? isAIEnabled ? t('conversations.disabling') : t('conversations.enabling') : t('common.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
};
export { ToggleAIConversationDialog };